import { sleep, setOverlayHeight, betterScrollIntoView } from '../../assets/js/utils.js';

class MedienOverlay extends HTMLElement {
    
    connectedCallback() {
        this.globaleVariablenDeklarieren();
        this.medienOverlayListener();
        this.kontrollelementeListener();
        this.thumbnailListener();
        this.initPreviewObserver();
        this.initThumbnailObserver();
        
        this.addEventListener('keydown', event => this.keydownHandler(event));
        
        window.addEventListener('resize', () => { 
            setOverlayHeight(this, '--overlay-height');
        });
        setOverlayHeight(this, '--overlay-height');
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'data-position' && newValue !== oldValue) {
            this.previewPositionHandler(newValue);
            this.thumbnailPositionHandler(newValue);
            this.thumbnailStatusToggle();
        }
    }

    previewPositionHandler(position) {
        this.querySelector(`[data-preview-index="${position}"]`).scrollIntoView();
    }

    async thumbnailPositionHandler(position) {
        this.thumbnailAktiv = this.querySelector(`[data-thumbnail-index="${position}"]`);
        // Wir müssen hier warten, da scrollIntoView mobil sonst nicht funktioniert.
        // Vermutlich kommt die API mit dem touch/scroll auf mobilen Geräten durcheinander.
        // Mit einem minimalen Timeout funktioniert es.
        await sleep(100);
        this.thumbnailAktiv.querySelector('input[name="thumbnails"]').checked = true;
        betterScrollIntoView(this.thumbnails, this.thumbnailAktiv); 
    }

    globaleVariablenDeklarieren() {
        this.overlay = this.querySelector(".medien-overlay");
        this.previewContainer = this.querySelector('.preview-container');
        this.thumbnailContainer = this.querySelector('.thumbnail-container');
        this.previews = this.querySelector('.previews');
        this.thumbnails = this.querySelector('.thumbnails');
        this.previewElement = this.querySelector('.preview');
        this.thumbnailElement = this.querySelector('.thumbnail');
        this.erstesPreviewElement = this.querySelector('.preview:first-of-type');
        this.letztesPreviewElement = this.querySelector('.preview:last-of-type');
        this.erstesThumbnailElement = this.querySelector('.thumbnail:first-of-type');
        this.letztesThumbnailElement = this.querySelector('.thumbnail:last-of-type');
        this.buttonVorherigesBild = this.querySelector('[interaction="nach-links-scrollen"]');
        this.buttonNaechstesBild = this.querySelector('[interaction="nach-rechts-scrollen"]');
        this.buttonVorherigeThumbnails = this.querySelector('[interaction="nach-oben-scrollen"]');
        this.buttonNaechsteThumbnails = this.querySelector('[interaction="nach-unten-scrollen"]');
    }

    kontrollelementeListener() {
        this.scrollOptions = {
            behavior: 'smooth'
        };
        this.buttonVorherigesBild.addEventListener('click', event => {
            this.previews.scrollTo(Object.assign(this.scrollOptions, {
                left: this.previews.scrollLeft - this.previewElement.offsetWidth,
            }))
        });
        this.buttonNaechstesBild.addEventListener('click', event => {
            this.previews.scrollTo(Object.assign(this.scrollOptions, {
                left: this.previews.scrollLeft + this.previewElement.offsetWidth,
            }))
        });
        this.buttonVorherigeThumbnails.addEventListener('click', event => {
            this.thumbnails.scrollTo(Object.assign(this.scrollOptions, {
                top: this.thumbnails.scrollTop - this.thumbnailElement.offsetWidth * 2,
            }))
        });
        this.buttonNaechsteThumbnails.addEventListener('click', event => {
            this.thumbnails.scrollTo(Object.assign(this.scrollOptions, {
                top: this.thumbnails.scrollTop + this.thumbnailElement.offsetWidth * 2,
            }))
        });
    }

    thumbnailListener() {
        this.querySelectorAll('.thumbnail').forEach(thumbnail => {
            thumbnail.addEventListener('click', event => {
                this.position = thumbnail.getAttribute("data-thumbnail-index");
                this.thumbnailStatusToggle();
            })
        })
    }

    thumbnailStatusToggle() {
        const thumbnails = this.querySelectorAll('.thumbnail');
        this.currentPosition = document.querySelector("medien-overlay").getAttribute("data-position");
        this.thumbnailAktiv = this.querySelector(`[data-thumbnail-index="${this.currentPosition}"]`);
        thumbnails.forEach(thumbnail => {
            thumbnail.removeAttribute('data-status');
        });
        this.thumbnailAktiv.setAttribute('data-status', 'active');
    }

    medienOverlayListener() {
        document.addEventListener("medienoverlay-oeffnen", (event) => {
            let { index } = event.detail;
            const { istEinzelbild } = event.detail;
            if (istEinzelbild && !this.hasAttribute("data-thumbnail-status")) {
                this.thumbnailStatus = "disabled";
            }
            if (isNaN(index)) {
                index = 0;
            }
            this.overlayOeffnen(index);
        });
    }

    overlayOeffnen(position) {
        this.overlay.showModal();
        this.querySelector('[interaction="medien-overlay-schliessen"]').addEventListener('click', event => {
            this.overlaySchliessen();
        });
        this.position = position;
        this.status = 'aktiv';
    }

    overlaySchliessen() {
        if (this.overlayIstGeoeffnet) {
            this.overlay.close();
            this.status = 'inaktiv';
            this.position = 0;
        }
    }

    keydownHandler(event) {
        if (event.code === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.overlaySchliessen();
        }
    }

    initPreviewObserver() {
        this.options = {
            root: this.previewContainer,
            threshold: [0.95, 0.96, 0.97, 0.98, 0.99, 1.0]
        };
        this.previewObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => this.previewIntersectionHandler(entry));
        }, this.options);
        this.querySelectorAll('.preview').forEach(element => this.previewObserver.observe(element));
    }

    previewIntersectionHandler(entry) {
        if (entry.intersectionRatio === 1 && this.status === 'aktiv') this.position = entry.target.dataset['previewIndex'];
        if (entry.target === this.erstesPreviewElement) {
            this.buttonVorherigesBild.setAttribute('data-status', entry.intersectionRatio === 1 ? 'inaktiv' : 'aktiv');
        }
        if (entry.target === this.letztesPreviewElement) {
            this.buttonNaechstesBild.setAttribute('data-status', entry.intersectionRatio === 1 ? 'inaktiv' : 'aktiv');
        }
    }

    initThumbnailObserver() {
        this.options = {
            root: this.thumbnailContainer,
            threshold: [0.95, 0.96, 0.97, 0.98, 0.99, 1.0]
        };
        this.thumbnailObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => this.thumbnailIntersectionHandler(entry));
        }, this.options);
        this.querySelectorAll('.thumbnail:first-of-type, .thumbnail:last-of-type').forEach(element => this.thumbnailObserver.observe(element));
    }

    thumbnailIntersectionHandler(entry) {
        if (entry.target === this.erstesThumbnailElement) {
            this.buttonVorherigeThumbnails.setAttribute('data-status', entry.intersectionRatio === 1 ? 'inaktiv' : 'aktiv');
        }
        if (entry.target === this.letztesThumbnailElement) {
            this.buttonNaechsteThumbnails.setAttribute('data-status', entry.intersectionRatio === 1 ? 'inaktiv' : 'aktiv');
        }
    }

    set status(neuerWert) {
        this.setAttribute('data-status', neuerWert)
    }

    get status() {
        return this.dataset['status'];
    }

    get overlayIstGeoeffnet() {
        return this.overlay.hasAttribute('open');
    }

    set position(neuerWert) {
        this.setAttribute('data-position', neuerWert);
    }

    set thumbnailStatus(value) {
        this.setAttribute("data-thumbnail-status", value);
    }

    static get observedAttributes() {
        return ['data-position'];
    }
}

if (!customElements.get("medien-overlay")) {
    customElements.define("medien-overlay", MedienOverlay);
}
